// UserSettings — a person's OWN account settings: how Harvus reaches them
// (notification channels + phone number), the Microsoft accounts they've
// connected (mail & calendar, To Do), quiet hours, the daily digests, and
// what Harvi is allowed to read on its own.
//
// EVERY AUTHENTICATED USER CAN REACH THIS — it is deliberately NOT gated on
// the Admin role, unlike Settings.jsx. Everything here writes only the
// signed-in user's own row, through the self-service endpoints that already
// existed (`GET`/`PUT /api/notification-preferences` since migration 78, and
// the `personal/*` + `todo/*` OAuth flows) but had nowhere a non-admin could
// reach them from: Settings is `adminOnly` in the nav, so these lived behind
// a ⚙ modal off the notification bell (channels/phone/quiet hours/Harvi) and
// on the admin-only Integrations tab (Mail & Calendar, To Do) — reachable to
// nobody but an Admin, or to a staff member who happened to find the bell.
//
// An Admin managing SOMEONE ELSE'S phone number, SMS/Teams flags, or role is
// a different capability and stays on Settings → Users — that is an admin
// acting on another person's account, not this page.
import { useCallback, useEffect, useState } from "react";
import { B, font, weight } from "../constants/brand";
import { api } from "../api/client";
import { setDisplayTimeZone } from "@harvus/shared";
import { useApi } from "../hooks/useApi";
import { Btn, Card, Select, Spinner, ErrorMsg, PageHeader, Badge, useToast } from "../components/ui";

// The hours a brief/recap/digest is worth offering. A free number box invites
// 25 and a blank; the API clamps either way, but a control that can express a
// value the server will reject is a control that lies about what it does.
const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: String(h),
  label: `${String(h).padStart(2, "0")}:00`,
}));

const Row = ({ label, description, checked, onChange, disabled, hint, badge }) => (
  <label
    style={{
      display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0",
      borderBottom: `1px solid ${B.divider}`, cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.55 : 1,
    }}
  >
    <input
      type="checkbox" checked={checked} disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      style={{ marginTop: 3, width: 16, height: 16, accentColor: B.navy, cursor: "inherit" }}
    />
    <span style={{ flex: 1 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.text }}>
          {label}
        </span>
        {badge || null}
      </span>
      {description ? (
        <span style={{ display: "block", fontSize: font.sm, color: B.textMd, marginTop: 2, lineHeight: 1.5 }}>
          {description}
        </span>
      ) : null}
      {hint ? (
        <span style={{ display: "block", fontSize: font.sm, color: B.textLt, marginTop: 4, lineHeight: 1.5 }}>
          {hint}
        </span>
      ) : null}
    </span>
  </label>
);

const CardTitle = ({ children }) => (
  <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>{children}</div>
);
const CardDesc = ({ children }) => (
  <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>{children}</div>
);
const SectionLabel = ({ children }) => (
  <div style={{ fontSize: font.sm, fontWeight: weight.bold, color: B.textMd, letterSpacing: 0.4, margin: "28px 0 10px" }}>
    {children}
  </div>
);

// "Run my brief now" is gated to one person while it's new — not a security
// boundary (POST /api/harvi/brief/run is self-service for any connected user
// already, see harviBrief.js), just controlling who sees an untested manual
// trigger. Same literal, same reasoning as the ownerEmail-gated nav items in
// constants/nav.js.
const BRIEF_RUN_NOW_EMAIL = "ngodfrey@harvus.com";

// ── Background mail sync — status line + "Sync my mail now" ─────────────────
// The sync (functions/mailThreadSync.js) runs every 15 minutes and on Graph
// push; this shows what it last did for THIS person and lets them run it now.
// A first run can outlast the ~45 s edge and carry on server-side, so a failed
// request is not the outcome: the status row is. We re-read it and report the
// request's own error only when the row shows no run happened since we pressed.
const SYNC_OUTCOME = {
  ok: null,
  'in-progress': "A sync is already running — it'll finish on its own.",
  'not-connected': 'Your Microsoft connection could not be used — reconnect above.',
  unavailable: 'Mail sync is not available on this deployment yet.',
  error: null,
};

const ago = (iso) => {
  if (!iso) return null;
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return new Date(iso).toLocaleDateString();
};

const describeSyncRun = (r) => {
  if (!r) return null;
  if (r.outcome && r.outcome !== 'ok') return SYNC_OUTCOME[r.outcome] || r.error || 'The sync did not finish.';
  const parts = [`${r.judged || 0} thread${r.judged === 1 ? '' : 's'} read`];
  if (r.alreadyCurrent) parts.push(`${r.alreadyCurrent} already up to date`);
  if (r.skippedNoise) parts.push(`${r.skippedNoise} automated skipped`);
  if (r.deferred) parts.push(`${r.deferred} left for the next run`);
  if (r.failed) parts.push(`${r.failed} could not be read`);
  return `Synced — ${parts.join(', ')}.`;
};

const MailSyncPanel = () => {
  const { data: st, loading, reload } = useApi(() => api.get('mail-sync/status'), []);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [resultTone, setResultTone] = useState('ok');

  const runNow = async () => {
    setRunning(true); setResult(null);
    const pressedAt = Date.now();
    try {
      const r = await api.post('mail-sync/run', {}, { timeout: 300000 });
      setResult(describeSyncRun(r));
      setResultTone(r?.outcome && r.outcome !== 'ok' ? 'warn' : 'ok');
    } catch {
      // The row is the truth: a run that landed after we pressed is the answer.
      let fresh = null;
      try { fresh = await api.get('mail-sync/status'); } catch { /* fall through */ }
      if (fresh?.lastSyncAt && new Date(fresh.lastSyncAt).getTime() >= pressedAt - 5000) {
        setResult(`Synced — ${fresh.lastJudged || 0} thread(s) read.`); setResultTone('ok');
      } else {
        setResult('Still working — the sync carries on in the background. Check back in a minute.');
        setResultTone('warn');
      }
    } finally { setRunning(false); reload(); }
  };

  if (loading || !st) return null;
  if (!st.available) {
    return (
      <div style={{ fontSize: 12, color: B.textLt, marginTop: 12 }}>
        Background mail sync is not set up on this deployment yet.
      </div>
    );
  }
  const last = ago(st.lastSyncAt);
  return (
    <div style={{
      marginTop: 14, padding: '10px 12px', background: B.surfaceMuted,
      border: `1px solid ${B.divider}`, borderRadius: 10,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: B.text }}>Harvi keeps up with your mail</div>
        <div style={{ fontSize: 12, color: B.textMd, marginTop: 2, lineHeight: 1.6 }}>
          {!st.enabled
            ? 'Background sync is paused for everyone right now.'
            : last
              ? <>Last synced {last}{st.lastJudged != null ? ` · ${st.lastJudged} thread${st.lastJudged === 1 ? '' : 's'} read` : ''}
                  {' · '}{st.needsYou ?? 0} need{st.needsYou === 1 ? 's' : ''} you
                  {' · '}{st.subscribed ? 'new mail pushed instantly' : 'checking every 15 minutes'}</>
              : 'Not synced yet — it runs every 15 minutes, or sync now.'}
        </div>
        {st.lastError && <div style={{ fontSize: 12, color: B.red, marginTop: 4 }}>{st.lastError}</div>}
        {st.subscriptionError && !st.subscribed && (
          <div style={{ fontSize: 12, color: B.amber, marginTop: 4 }}>
            New mail isn't pushed instantly ({st.subscriptionError}) — the 15-minute check still catches it.
          </div>
        )}
        {result && (
          <div style={{ fontSize: 12, color: resultTone === 'ok' ? B.green : B.amber, marginTop: 4 }}>{result}</div>
        )}
      </div>
      <Btn variant="ghost" onClick={runNow} loading={running} disabled={!st.enabled}>Sync my mail now</Btn>
    </div>
  );
};

// ── Mail & Calendar — Your Connection ───────────────────────────────────────
// The one connection behind Harvi's mail/calendar tools AND the dedicated
// Mail / Calendar pages — per-user opt-in, and a SEPARATE consent from To Do
// below. They are different provider rows keyed (UserID, Provider), so
// connecting a task list must never quietly grant a mailbox, and
// disconnecting one must not take the other with it. Deliberately the same
// card shape as To Do: two consents that read differently would invite the
// reader to assume one is the lesser of the two. (Moved here from the
// admin-only Settings → Integrations tab — this is a personal connection,
// not an org-wide switch.)
const PersonalConnectCard = () => {
  const { toast } = useToast();
  const { data: status, loading, reload } = useApi(() => api.get('personal/status'), []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const r = q.get('personal');
    if (!r) return;
    if (r === 'connected') { toast.success('Mail and calendar connected'); reload(); }
    else if (r === 'error') {
      const reason = q.get('reason');
      toast.error(reason ? `Could not connect mail and calendar — ${reason}` : 'Could not connect mail and calendar');
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('personal');
    url.searchParams.delete('reason');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true);
    try { const { url } = await api.get('personal/connect'); window.location.href = url; }
    catch (e) { toast.error(e.message || 'Could not start the connection'); setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.post('personal/disconnect', {}); toast.success('Disconnected'); reload(); }
    catch { toast.error('Could not disconnect'); }
    finally { setBusy(false); }
  };

  if (loading) return null;
  const connected = status?.connected;
  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: B.text }}>Mail &amp; Calendar</div>
          <div style={{ fontSize: 13, color: B.textMd, marginTop: 2 }}>
            {connected
              ? 'Connected — Harvi can search your own Outlook mail and calendar, and the Mail / Calendar pages use it too.'
              : 'Connect your Microsoft account to use the Mail and Calendar pages, and let Harvi search your mail and calendar in a private thread.'}
          </div>
          {status?.needsReconnect && (
            <div style={{ fontSize: 12, color: B.amber, marginTop: 4 }}>
              Your sign-in has expired — reconnect to carry on.
            </div>
          )}
          {/* A connection made before the Mail page could send mail is still
              missing the Mail.Send scope — Microsoft will not hand back an
              access token carrying a scope nobody consented to, so this is a
              reconnect, not a fault. */}
          {connected && !status?.needsReconnect && !status?.canSendMail && (
            <div style={{ fontSize: 12, color: B.amber, marginTop: 4 }}>
              Reconnect to let the Mail page send on your behalf — your current connection predates that.
            </div>
          )}
          {connected && !status?.needsReconnect && status?.lastError && (
            <div style={{ fontSize: 12, color: B.red, marginTop: 4 }}>{status.lastError}</div>
          )}
        </div>
        {!status?.configured
          ? <Badge color={B.textLt}>Not available on this deployment</Badge>
          : connected
            ? <Btn variant="ghost" onClick={disconnect} loading={busy}>Disconnect</Btn>
            : <Btn variant="primary" onClick={connect} loading={busy}>Connect mail &amp; calendar</Btn>}
      </div>

      {connected && !status?.needsReconnect && <MailSyncPanel />}

      {/* WHAT IS BEING GRANTED, IN WORDS. Microsoft's consent screen shows
          scope NAMES, and a person reads "Mail.ReadWrite" as including the
          ability to send — so the one guarantee that matters most is the one
          its own screen states least clearly. */}
      <ul style={{ margin: '12px 0 0', paddingLeft: 20, fontSize: 12, color: B.textMd, lineHeight: 1.7 }}>
        {(status?.consent || []).map(line => <li key={line}>{line}</li>)}
      </ul>
      <div style={{ fontSize: 12, color: B.textMd, marginTop: 8 }}>
        Only you can see any of this: mail and calendar work only in a Harvi thread that is
        private to you and not attached to a deal, and once a thread has read them it can
        never afterwards be shared.
      </div>
    </Card>
  );
};

// Microsoft To Do connection card — per-user opt-in. Connecting starts an
// OAuth consent (offline_access + Tasks.ReadWrite) so Harvus can two-way sync
// this user's tasks into their To Do "Harvus" list in the background. The
// org-wide "Auto To Do Sync" switch stays on Settings → Integrations (Admin)
// — that one pauses the background timer for everybody; this one is whether
// YOUR tasks are in it at all.
const TodoConnectCard = () => {
  const { toast } = useToast();
  const { data: status, loading, reload } = useApi(() => api.get('todo/status'), []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const r = q.get('todo');
    if (!r) return;
    if (r === 'connected') { toast.success('Microsoft To Do connected'); reload(); }
    else if (r === 'error') {
      const reason = q.get('reason');
      toast.error(reason ? `Could not connect Microsoft To Do — ${reason}` : 'Could not connect Microsoft To Do');
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('todo');
    url.searchParams.delete('reason');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true);
    try { const { url } = await api.get('todo/connect'); window.location.href = url; }
    catch { toast.error('Could not start To Do connection'); setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.post('todo/disconnect', {}); toast.success('Disconnected'); reload(); }
    catch { toast.error('Could not disconnect'); }
    finally { setBusy(false); }
  };

  if (loading) return null;
  const connected = status?.connected;
  return (
    <Card style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text }}>Microsoft To Do</div>
        <div style={{ fontSize: 13, color: B.textMd, marginTop: 2 }}>
          {connected
            ? 'Connected — your tasks sync to your To Do "Harvus" list.'
            : 'Connect to sync your tasks to Microsoft To Do (and check them off from anywhere).'}
        </div>
        {connected && status?.lastError && (
          <div style={{ fontSize: 12, color: B.red, marginTop: 4 }}>{status.lastError}</div>
        )}
      </div>
      {connected
        ? <Btn variant="ghost" onClick={disconnect} loading={busy}>Disconnect</Btn>
        : <Btn variant="primary" onClick={connect} loading={busy}>Connect To Do</Btn>}
    </Card>
  );
};

export const UserSettings = ({ user }) => {
  const { toast } = useToast();
  const [prefs, setPrefs] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [runningBrief, setRunningBrief] = useState(false);
  const [briefRunResult, setBriefRunResult] = useState(null); // { tone, text }

  // The local editable copy, so a half-typed edit never gets saved by accident
  // and the form doesn't fight a background reload.
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get("notification-preferences");
      setPrefs(res);
      setDraft({
        email: !!res.email, teams: !!res.teams, text: !!res.text, push: !!res.push,
        phone: res.phone || "",
        morningBrief: !!res.morningBrief?.enabled,
        morningBriefHour: String(res.morningBrief?.hour ?? 7),
        webResearch: !!res.webResearch?.enabled,
        // Quiet hours: on when both bounds are set. Defaults for the pickers
        // are the recommended 10 PM – 7 AM, shown only once switched on.
        quietOn: res.quietHours?.start != null && res.quietHours?.end != null,
        quietStart: String(res.quietHours?.start ?? 22),
        quietEnd: String(res.quietHours?.end ?? 7),
        dailyRecap: !!res.dailyRecap,
        recapHour: String(res.recapHour ?? 17),
        activityDigest: !!res.activityDigest,
        activityDigestHour: String(res.activityDigestHour ?? 17),
        // "" means "use the app default" — the server treats an empty string
        // the same way (clears the column), so there is no third value to track.
        timeZone: res.timeZone || "",
      });
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!draft) return;
    setSaving(true); setError(null);
    try {
      const { quietOn, quietStart, quietEnd, ...rest } = draft;
      const res = await api.put("notification-preferences", {
        ...rest,
        // The hour selects are strings; the API wants a number and clamps it,
        // but sending "7" where an integer is meant is how a value ends up
        // stored as text somewhere downstream.
        morningBriefHour: Number(draft.morningBriefHour),
        recapHour: Number(draft.recapHour),
        activityDigestHour: Number(draft.activityDigestHour),
        // Sent only where the server can store it. ONE object, or null to
        // clear: a lone bound is not a range, and the API refuses half of one.
        ...(prefs?.quietHours?.available
          ? { quietHours: quietOn ? { start: Number(quietStart), end: Number(quietEnd) } : null }
          : {}),
      });
      setPrefs(res);
      // Re-render every date in the zone just saved (blank → the browser's).
      setDisplayTimeZone(res?.timeZone || null);
      toast.success("Saved.");
    } catch (e) { setError(e.message); toast.error(e.message); }
    finally { setSaving(false); }
  };

  // Fires the SAME turn the 7am timer runs (POST /api/harvi/brief/run), for
  // whoever is signed in — it needs no MorningBriefEnabled toggle, just a
  // connected mailbox. A long timeout: this is a real Harvi turn with tool
  // calls (mail search, possibly web), the same budget ChatPanel gives one.
  const runBriefNow = async () => {
    setRunningBrief(true); setBriefRunResult(null);
    try {
      const res = await api.post("harvi/brief/run", {}, { timeout: 300000 });
      if (res?.sent) {
        const n = res.unreadCount;
        setBriefRunResult({
          tone: "green",
          text: (n != null
            ? `Sent — Harvi looked at ${n} mail result${n === 1 ? "" : "s"}. `
            : "Sent. ") + "Open Harvi to read it.",
        });
      } else if (res?.skipped) {
        setBriefRunResult({ tone: "amber", text: res.reason || "Skipped." });
      } else {
        setBriefRunResult({ tone: "red", text: res?.error || "The brief could not be generated." });
      }
    } catch (e) {
      setBriefRunResult({ tone: "red", text: e.message });
    } finally {
      setRunningBrief(false);
    }
  };

  const useDeviceTimeZone = () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) set("timeZone", tz);
    } catch { /* Intl unsupported — leave the field alone */ }
  };

  const brief = prefs?.morningBrief;
  const web = prefs?.webResearch;
  const showHarvi = !!(brief?.available || web?.available);
  const canRunBriefNow = (user?.email || "").trim().toLowerCase() === BRIEF_RUN_NOW_EMAIL;

  return (
    <div>
      <PageHeader
        icon="user"
        title="My Settings"
        subtitle="How Harvus reaches you, your phone number, and the accounts you've connected."
        actions={draft ? <Btn onClick={save} disabled={saving} loading={saving}>Save</Btn> : null}
      />

      {!draft && !error && <Spinner label="Loading your settings…" />}
      {error && <ErrorMsg msg={error} onRetry={load} />}

      {draft && (
        <>
          <Card style={{ marginBottom: 20 }}>
            <CardTitle>Notifications</CardTitle>
            <CardDesc>
              Where Harvus reaches you. These apply to everything — task assignments, deal
              activity, mentions. Notifications are grouped and delivered every 15 minutes as
              one message per channel; only Harvi&rsquo;s own prompts arrive at once. The bell
              in the header still updates as things happen.
            </CardDesc>

            <Row
              label="In-app and browser push"
              description="A notification on this device, plus the bell in the header."
              checked={draft.push} onChange={(v) => set("push", v)}
            />
            <Row
              label="Email"
              checked={draft.email} onChange={(v) => set("email", v)}
            />
            {/* Its own opt-in, separate from the Email channel above: turning
                general email off does not stop this report, and this report is
                never sent anywhere but email. */}
            <Row
              label="Daily activity brief email"
              description="A PDF summary of the last 24 hours of activity across all deals, emailed to you every day — on its own, whatever the Email setting above says."
              checked={draft.activityDigest} onChange={(v) => set("activityDigest", v)}
            />
            {draft.activityDigest ? (
              <div style={{ maxWidth: 220, margin: "12px 0" }}>
                <Select label="Send it at" value={draft.activityDigestHour} onChange={(v) => set("activityDigestHour", v)} options={HOURS} />
              </div>
            ) : null}
            <Row
              label="Microsoft Teams"
              description={prefs.teamsRegistered
                ? "Direct message from the Harvus bot."
                : "Message the Harvus bot in Teams once to enable this."}
              checked={draft.teams} onChange={(v) => set("teams", v)}
              disabled={!prefs.teamsRegistered}
              badge={
                <Badge color={prefs.teamsRegistered ? B.green : B.textLt}>
                  {prefs.teamsRegistered ? "Bot registered" : "Not registered yet"}
                </Badge>
              }
            />
            <Row
              label="Text messages (SMS)"
              description="Opt-in. Needs a mobile number below."
              checked={draft.text} onChange={(v) => set("text", v)}
              disabled={!draft.phone.trim()}
              hint={!draft.phone.trim() ? "Add a number below to enable text messages." : null}
            />

            <div style={{ paddingTop: 14 }}>
              <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.textMd, marginBottom: 6 }}>
                Mobile number
              </div>
              <input
                type="tel" value={draft.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+1 555 555 1234"
                style={{
                  width: "100%", maxWidth: 320, border: `1px solid ${B.border}`, borderRadius: 6,
                  padding: "8px 12px", fontSize: font.base, fontFamily: "inherit", outline: "none",
                }}
              />
            </div>
          </Card>

          <SectionLabel>CONNECTED ACCOUNTS</SectionLabel>
          <div style={{ fontSize: 13, color: B.textMd, marginBottom: 12 }}>
            These connect or disconnect immediately — there&rsquo;s nothing to save here.
          </div>
          <PersonalConnectCard />
          <TodoConnectCard />

          <Card style={{ marginBottom: 20 }}>
            <CardTitle>Time zone &amp; quiet hours</CardTitle>
            <CardDesc>
              Used for quiet hours below, and for every &ldquo;send it at&rdquo; hour picker
              further down the page.
            </CardDesc>

            <div style={{ marginBottom: prefs?.quietHours?.available ? 18 : 0 }}>
              <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.textMd, marginBottom: 6 }}>
                Time zone
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <input
                  value={draft.timeZone}
                  onChange={(e) => set("timeZone", e.target.value)}
                  placeholder="e.g. America/Chicago"
                  style={{
                    flex: "1 1 220px", border: `1px solid ${B.border}`, borderRadius: 6,
                    padding: "8px 12px", fontSize: font.base, fontFamily: "inherit", outline: "none",
                  }}
                />
                <Btn type="button" variant="secondary" size="sm" onClick={useDeviceTimeZone}>
                  Use this device&rsquo;s time zone
                </Btn>
              </div>
              <div style={{ fontSize: font.sm, color: B.textLt, marginTop: 6, lineHeight: 1.5 }}>
                Set automatically the first time you sign in on the iPhone app. Leave blank to
                use the app default.
              </div>
            </div>

            {/* QUIET HOURS hold the digest overnight. Rendered only where the
                schema can store it (`available`), and the two pickers only
                once it is on — a time range for something that is not
                happening is a control that does nothing. */}
            {prefs?.quietHours?.available ? (
              <>
                <Row
                  label="Hold notifications overnight"
                  description="Nothing is delivered between these hours in your time zone. Whatever happened arrives as one batch at the first quarter-hour after they end."
                  checked={draft.quietOn} onChange={(v) => set("quietOn", v)}
                  hint="The bell and the in-app inbox still fill in real time. Harvi's own prompts are not held."
                />
                {draft.quietOn ? (
                  <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
                    <div style={{ width: 160 }}>
                      <Select label="From" value={draft.quietStart} onChange={(v) => set("quietStart", v)} options={HOURS} />
                    </div>
                    <div style={{ width: 160 }}>
                      <Select label="Until" value={draft.quietEnd} onChange={(v) => set("quietEnd", v)} options={HOURS} />
                    </div>
                    {draft.quietStart === draft.quietEnd ? (
                      <div style={{ fontSize: font.sm, color: B.amber, alignSelf: "flex-end", paddingBottom: 8 }}>
                        Start and end are the same hour, so nothing is held.
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : null}
          </Card>

          <Card style={{ marginBottom: 20 }}>
            <CardTitle>Daily recap</CardTitle>
            <CardDesc>An optional prompt at an hour you pick, in your time zone above.</CardDesc>

            <Row
              label="Daily recap prompt"
              description={'A "what happened today?" message each afternoon, on whichever channels are on above — your reply is captured as an ordinary Harvi conversation.'}
              checked={draft.dailyRecap} onChange={(v) => set("dailyRecap", v)}
            />
            {draft.dailyRecap ? (
              <div style={{ maxWidth: 220, margin: "12px 0" }}>
                <Select label="Send it at" value={draft.recapHour} onChange={(v) => set("recapHour", v)} options={HOURS} />
              </div>
            ) : null}

          </Card>

          {/* HARVI'S OWN FEATURES are not channels and are not grouped with
              them above. A channel decides where a notification lands; these
              decide what Harvi is allowed to read and do on its own, every
              morning or every chat. Rendered only where the schema can store
              them — `available` is a third state, and a control that cannot
              be saved is worse than none. */}
          {showHarvi ? (
            <Card>
              <CardTitle>Harvi</CardTitle>
              <CardDesc>What Harvi may read or search on its own, unprompted.</CardDesc>

              {web?.available ? (
                <Row
                  label="Web research"
                  description="Lets Harvi search the public internet when Harvus records cannot answer. Every web fact is attributed to the page it came from and said to be from the web."
                  checked={draft.webResearch} onChange={(v) => set("webResearch", v)}
                  hint="This is your default for new chats. Any chat can switch it on or off from its thread menu."
                />
              ) : null}
              {brief?.available ? (
                <>
                  <Row
                    label="Daily brief"
                    description="Each morning Harvi reads your unread mail and your calendar and writes a brief in a thread private to you."
                    checked={draft.morningBrief} onChange={(v) => set("morningBrief", v)}
                    hint={"Needs your Microsoft mail and calendar connected above. "
                        + "The notification says a brief is ready and nothing about what is in it."}
                  />
                  {draft.morningBrief ? (
                    <div style={{ maxWidth: 220, marginTop: 12 }}>
                      <Select
                        label="Send it at"
                        value={draft.morningBriefHour}
                        onChange={(v) => set("morningBriefHour", v)}
                        options={HOURS}
                      />
                    </div>
                  ) : null}
                  {/* Manual trigger, Neil-only while it's new — see
                      BRIEF_RUN_NOW_EMAIL above. Works whether or not the
                      toggle above is on: it needs a connected mailbox, not
                      the opt-in. */}
                  {canRunBriefNow ? (
                    <div style={{ marginTop: 12 }}>
                      <Btn variant="secondary" size="sm" onClick={runBriefNow} loading={runningBrief} disabled={runningBrief}>
                        Run my brief now
                      </Btn>
                      {briefRunResult ? (
                        <div style={{
                          fontSize: font.sm, marginTop: 8, lineHeight: 1.5,
                          color: briefRunResult.tone === "green" ? B.green
                            : briefRunResult.tone === "amber" ? B.amber : B.red,
                        }}>
                          {briefRunResult.text}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : null}
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
};

export default UserSettings;
